import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const UIContext = createContext();

export const UIProvider = ({ children }) => {
    // Current active tab
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // Team section states
    const [teamTab, setTeamTab] = useState('direct');
    const [currentLevel, setCurrentLevel] = useState(1);
    
    // Activities section states
    const [activitiesPage, setActivitiesPage] = useState(1);
    const [totalActivitiesPages, setTotalActivitiesPages] = useState(1);
    const [activityType, setActivityType] = useState('all');
    
    // Modal state
    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState({
        title: '',
        message: '',
        onClose: null
    });
    
    // Loading state
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    
    // AI Assistant state
    const [aiMessageVisible, setAiMessageVisible] = useState(false);
    const [aiMessage, setAiMessage] = useState('Welcome to GrichInv. How can I assist you today?');
    
    // Mobile responsive state
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    // Network error state
    const [networkError, setNetworkError] = useState(false);
    
    // Show loading with message
    const showLoading = (message = 'Loading...') => {
        setLoadingMessage(message);
        setIsLoading(true);
    };
    
    // Hide loading
    const hideLoading = () => {
        setIsLoading(false);
    };
    
    // Show modal
    const showModal = (title, message, onClose = null) => {
        setModalContent({
            title,
            message,
            onClose
        });
        setModalVisible(true);
    };
    
    // Hide modal
    const hideModal = () => {
        setModalVisible(false);
        if (modalContent.onClose) {
            modalContent.onClose();
        }
    };
    
    // Show AI message
    const showAiMessage = (message, duration = 5000) => {
        setAiMessage(message);
        setAiMessageVisible(true);
        
        if (duration > 0) {
            setTimeout(() => {
                setAiMessageVisible(false);
            }, duration);
        }
    };
    
    // Toggle AI message visibility
    const toggleAiMessage = () => {
        setAiMessageVisible(!aiMessageVisible);
        
        if (!aiMessageVisible) {
            // Auto-hide after 5 seconds when showing
            setTimeout(() => {
                setAiMessageVisible(false);
            }, 5000);
        }
    };
    
    // Toggle mobile menu
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };
    
    // Check if the screen is mobile size
    const [isMobile, setIsMobile] = useState(false);
    
    // Update mobile state based on screen width
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };
        
        // Initial check
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    // Auto close mobile menu when changing tabs
    useEffect(() => {
        if (isMobileMenuOpen) {
            setIsMobileMenuOpen(false);
        }
    }, [activeTab]);
    
    // Value object to be provided by the context
    const value = {
        // States
        activeTab,
        teamTab,
        currentLevel,
        activitiesPage,
        totalActivitiesPages,
        activityType,
        modalVisible,
        modalContent,
        isLoading,
        loadingMessage,
        aiMessageVisible,
        aiMessage,
        isMobileMenuOpen,
        isMobile,
        networkError,
        
        // Setters
        setActiveTab,
        setTeamTab,
        setCurrentLevel,
        setActivitiesPage,
        setTotalActivitiesPages,
        setActivityType,
        setNetworkError,
        
        // Actions
        showLoading,
        hideLoading,
        showModal,
        hideModal,
        showAiMessage,
        toggleAiMessage,
        toggleMobileMenu
    };
    
    return (
        <UIContext.Provider value={value}>
            {children}
        </UIContext.Provider>
    );
};

// Custom hook to use the UI context
export const useUI = () => {
    const context = useContext(UIContext);
    if (context === undefined) {
        throw new Error('useUI must be used within a UIProvider');
    }
    return context;
};

export default UIContext;